import React from 'react'
import styled from 'styled-components'

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    
    &::after {
      opacity: 0.1;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
`

const ProjectType = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 1rem;
  background: ${props => {
    switch (props.$type) {
      case 'x_management': return 'rgba(59, 130, 246, 0.2)'
      case 'github': return 'rgba(16, 185, 129, 0.2)'
      case 'website': return 'rgba(249, 115, 22, 0.2)'
      default: return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'x_management': return 'var(--color-primary)'
      case 'github': return 'var(--color-secondary)'
      case 'website': return 'var(--color-accent)'
      default: return 'var(--color-text)'
    }
  }};
`

const ProjectTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-text);
  margin-bottom: 0.5rem;
`

const ProjectStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--color-text-secondary);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => {
      switch (props.$status) {
        case 'active': return 'var(--color-primary)'
        case 'completed': return 'var(--color-secondary)'
        case 'draft': return 'var(--color-accent)'
        default: return 'var(--color-text-secondary)'
      }
    }};
  }
`

const ProjectDate = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: 1rem;
`

const Links = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`

const LinkIcon = styled.a`
  color: var(--color-text-secondary);
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const AgentCount = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.875rem;
  color: var(--color-text-secondary);

  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const ProjectCard = ({ project, onClick }) => {
  const getTypeLabel = (type) => {
    switch (type) {
      case 'x_management': return 'X Account Management'
      case 'github': return 'GitHub Repository'
      case 'website': return 'Website Development'
      default: return type
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Card onClick={() => onClick(project)}>
      <ProjectType $type={project.type}>
        {getTypeLabel(project.type)}
      </ProjectType>
      <ProjectTitle>{project.name}</ProjectTitle>
      <ProjectStatus $status={project.status}>
        {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
      </ProjectStatus>
      <ProjectDate>
        {project.status === 'draft' ? 'Last modified' : 'Deployed'}: {formatDate(project.date)}
      </ProjectDate>
      
      <AgentCount>
        <span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L4 8H20L12 16Z" fill="currentColor"/>
          </svg>
          {project.agents.coders} Coders
        </span>
        <span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L4 8H20L12 16Z" fill="currentColor"/>
          </svg>
          {project.agents.artists} Artists
        </span>
        <span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L4 8H20L12 16Z" fill="currentColor"/>
          </svg>
          {project.agents.social} Social
        </span>
      </AgentCount>

      {project.links && (
        <Links>
          {project.links.github && (
            <LinkIcon href={project.links.github} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
              </svg>
            </LinkIcon>
          )}
          {project.links.website && (
            <LinkIcon href={project.links.website} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </LinkIcon>
          )}
          {project.links.social && (
            <LinkIcon href={project.links.social} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
              </svg>
            </LinkIcon>
          )}
        </Links>
      )}
    </Card>
  )
}

export default ProjectCard
